import { useState, useRef, useEffect, useCallback } from 'react'
import { useInView } from 'react-intersection-observer'
import {
  PDFRendererProps,
  PDF_SCROLLABLE_CONTAINER_ID,
} from '../components/PDFViewer'
import { encodeBoundaryKey } from '../utils/boundaryKey'
import { usePDFStore } from '../stores/pdf.store'

type DocumentPage = PDFRendererProps['pdf']['pages'][number]

export const useMeasurePage = <T extends HTMLCanvasElement | HTMLImageElement>({
  pageRef,
  onPageViewportIn,
  onPageBoundaryChange,
  index,
  documentPage,
}: {
  pageRef: React.RefObject<T>
  index: number
  documentPage: DocumentPage
} & Pick<PDFRendererProps, 'onPageViewportIn' | 'onPageBoundaryChange'>) => {
  const zoom = usePDFStore((state) => state.zoom)

  const [isLoading, setIsLoading] = useState(true)

  const { ref: inViewRef, inView } = useInView({
    threshold: 0.1,
    root: window.document.getElementById(PDF_SCROLLABLE_CONTAINER_ID)!,
  })

  const scrollableContainerContentHeight = usePDFStore(
    (state) => state.scrollableContainerContentHeight
  )

  const recalculateBounds = useCallback(() => {
    if (!onPageBoundaryChange) return

    const scrollableContainer = window.document.getElementById(
      PDF_SCROLLABLE_CONTAINER_ID
    )

    const bounds = pageRef.current!.getBoundingClientRect()

    onPageBoundaryChange(encodeBoundaryKey(documentPage), {
      width: bounds.width,
      height: bounds.height,
      pageIndex: index,
      originalPageHeight: documentPage.height,
      originalPageWidth: documentPage.width,
      x: bounds.x + scrollableContainer!.scrollLeft,
      y: bounds.y + scrollableContainer!.scrollTop,
      left: bounds.left + scrollableContainer!.scrollLeft,
      top: bounds.top + scrollableContainer!.scrollTop,
    })
  }, [])

  useEffect(() => {
    if (inView && onPageViewportIn) {
      onPageViewportIn(index)
    }
  }, [inView])

  useEffect(() => {
    if (!isLoading && onPageBoundaryChange) {
      recalculateBounds()
    }
  }, [isLoading, zoom, scrollableContainerContentHeight])

  useEffect(() => {
    window.addEventListener('resize', recalculateBounds)

    return () => window.removeEventListener('resize', recalculateBounds)
  }, [])

  return {
    inViewRef,
    setIsLoading,
    isLoading,
  }
}
